import type React from 'react';

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
} from 'lexical';
import { DecoratorNode } from 'lexical';

import type {
  ActionType,
  ActionTypePrefix,
} from '@polyai/stream/utils/rich-text-input';

import Action from 'components/molecules/LexicalEditor/features/action/components/Action';
import { prefixMap } from 'components/molecules/LexicalEditor/features/action/constants';

export type SerializedActionNode = SerializedLexicalNode & {
  prefix: string;
  actionId: string;
};

export class ActionNode extends DecoratorNode<React.ReactElement> {
  __prefix: string;
  __actionId: string;
  constructor({
    prefix,
    actionId,
    key,
  }: {
    prefix: string;
    actionId: string;
    key?: NodeKey;
  }) {
    super(key);
    this.__prefix = prefix;
    this.__actionId = actionId;
  }

  static getType(): string {
    return 'action';
  }

  static clone(node: ActionNode): ActionNode {
    return new ActionNode({
      prefix: node.__prefix,
      actionId: node.__actionId,
      key: node.__key,
    });
  }

  static importJSON(serializedNode: SerializedActionNode): ActionNode {
    return new ActionNode({
      prefix: serializedNode.prefix,
      actionId: serializedNode.actionId,
    });
  }

  static getActionType(prefix: ActionTypePrefix): ActionType {
    return prefixMap[prefix];
  }

  exportJSON(): SerializedActionNode {
    return {
      ...super.exportJSON(),
      actionId: this.__actionId,
      prefix: this.__prefix,
      type: 'action',
      version: 1,
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'action-tag-node';
    span.style.display = 'inline';
    return span;
  }

  exportDOM(_editor: LexicalEditor): DOMExportOutput {
    const span = document.createElement('span');
    span.className = 'action-tag-node';

    span.setAttribute(
      'data-action-tag',
      JSON.stringify({ prefix: this.__prefix, actionId: this.__actionId }),
    );

    span.textContent = this.getTextContent();

    return { element: span };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: Node) => {
        const element = node as HTMLElement;
        const data = element.getAttribute('data-action-tag');
        if (!data) {
          return null;
        }

        return {
          conversion: (el): DOMConversionOutput => {
            const data = el.getAttribute('data-action-tag');
            if (!data) {
              return { node: null };
            }

            const { prefix, actionId } = JSON.parse(data);

            return {
              node: $createActionNode({ prefix, id: actionId }),
            };
          },
          priority: 0,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  decorate(): React.ReactElement {
    return (
      <Action
        id={this.__actionId}
        nodeKey={this.__key}
        type={ActionNode.getActionType(this.__prefix as ActionTypePrefix)}
      />
    );
  }

  getTextContent(): string {
    // this is mainly due to the way placeholders are created in the action picker.
    if (!this.__prefix || !this.__actionId) {
      return '';
    }

    return `{{${this.__prefix}${this.__actionId}}}`;
  }
}

export function $createActionNode({
  prefix,
  id,
}: {
  prefix: string;
  id: string;
}): ActionNode {
  return new ActionNode({
    prefix,
    actionId: id,
  });
}

export function $isActionNode(
  node: LexicalNode | null | undefined,
): node is ActionNode {
  return node instanceof ActionNode;
}
